import React, { useEffect, useState } from "react";

const PrivacyPolicy: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6 md:px-12 fixed w-full z-[1000] top-0">
        <nav className="container mx-auto flex justify-between items-center">
          <a
            href="/"
            className="text-2xl font-bold text-brand-dark-blue rounded-md p-2 hover:bg-gray-100 transition-colors"
          >
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10"
            />
          </a>
          <div className="hidden md:flex space-x-6">
            <a
              href="/"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
            >
              Home
            </a>
            <a
              href="/login"
              className="text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md px-4 py-2 ml-4 shadow-md hover:scale-105 duration-200"
            >
              Log In
            </a>
          </div>
          <button
            className="md:hidden text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-dark-blue rounded-md p-2"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </nav>
        <div
          className={`md:hidden bg-white py-2 mt-2 border-t border-gray-200${
            mobileMenuOpen ? "" : " hidden"
          }`}
        >
          <a
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
          >
            Home
          </a>
          <a
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md mx-2 mt-2 shadow-md text-center"
          >
            Log In
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <section className="bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white py-20 px-6 md:px-12 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Privacy Policy
            </h1>
            <p className="text-xl opacity-90">Last updated: April 2026</p>
          </div>
        </section>

        <section className="py-16 px-6 md:px-12 bg-white">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Introduction
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Amblyfy ("we," "our," or "us") is a kids' habit-tracking app
                  managed by parents/guardians. This Privacy Policy explains
                  what information we collect, how we use it, and the choices
                  you have. Amblyfy is not a medical app and should not be used
                  to store medical history or health information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Information We Collect
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="text-xl font-semibold text-brand-cyan mb-2">
                      Account and Profile Information
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>
                        Parent/guardian name and contact information (e.g.,
                        email)
                      </li>
                      <li>
                        Child profile details provided by a parent/guardian
                        (e.g., first name or nickname, avatar)
                      </li>
                      <li>
                        Login credentials (stored securely; passwords are
                        hashed)
                      </li>
                    </ul>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brand-cyan mb-2">
                      App Activity and Device Information
                    </h3>
                    <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                      <li>
                        Routines, goals, schedules, rewards, and completion logs
                      </li>
                      <li>
                        App usage events, crash logs, and performance metrics
                      </li>
                      <li>Device type, operating system, and app version</li>
                      <li>
                        Session interaction data (screen views, taps, and usage
                        patterns) collected via Microsoft Clarity to help us
                        improve the app experience
                      </li>
                    </ul>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed mt-4">
                  We ask parents/guardians not to enter medical or health
                  history. Amblyfy does not process medical data.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  How We Use Information
                </h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Provide and improve Amblyfy’s habit-tracking features</li>
                  <li>Sync data across devices and enable backups</li>
                  <li>Personalize your in-app experience</li>
                  <li>
                    Maintain safety, security, and integrity (e.g., fraud
                    prevention)
                  </li>
                  <li>
                    Communicate with parents/guardians about the account and
                    updates
                  </li>
                  <li>Comply with legal obligations</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  We do not sell personal information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Children’s Privacy and Parental Controls
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  Amblyfy is intended for children to use with supervision by a
                  parent or guardian who creates and manages the account. Where
                  required by law, we obtain verifiable parental consent before
                  collecting personal information from a child.
                  Parents/guardians can review, update, export, or delete their
                  child’s information by contacting us or using in-app controls
                  where available. We do not use a child’s personal information
                  for targeted advertising or profiling.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Information Sharing
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We share information only as needed to operate and improve
                  Amblyfy:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>
                    <strong>Service Providers:</strong> Cloud hosting,
                    authentication, analytics, and support tools bound by
                    confidentiality and security obligations
                  </li>
                  <li>
                    <strong>Legal and Safety:</strong> When required by law or
                    to protect rights, safety, and property
                  </li>
                  <li>
                    <strong>Business Transfers:</strong> If we undergo a merger,
                    acquisition, or asset sale, with notice where legally
                    required
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  We do not sell personal information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Analytics and Session Recording
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We use <strong>Microsoft Clarity</strong>, a behavioural
                  analytics service provided by Microsoft Corporation, to
                  understand how users interact with our app. Microsoft Clarity
                  may collect:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4 mb-4">
                  <li>Session recordings (touch interactions and screen flows)</li>
                  <li>Heatmaps and tap patterns</li>
                  <li>Custom events we define (e.g. timer started, goal met)</li>
                  <li>
                    A pseudonymous user identifier linked to your Amblyfy account
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  Microsoft Clarity data is processed by Microsoft and subject to
                  the{" "}
                  <a
                    href="https://privacy.microsoft.com/en-us/privacystatement"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-cyan underline"
                  >
                    Microsoft Privacy Statement
                  </a>
                  . We use this data solely to improve the app experience and do
                  not use it for advertising or profiling. No raw session
                  recordings include passwords or payment information.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Data Security
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We implement reasonable administrative, technical, and
                  physical safeguards to help protect information. However, no
                  method of transmission or storage is 100% secure.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Data Retention
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We retain information while your account is active or as
                  needed to provide the service. You may request deletion of
                  your or your child’s data, subject to legal or legitimate
                  retention needs (for example, security logs).
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Your Rights and Choices
                </h2>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Access, correct, or delete your information</li>
                  <li>Object to or restrict processing in certain cases</li>
                  <li>Export your data (data portability)</li>
                  <li>Withdraw consent where processing is based on consent</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  To exercise these rights, contact us using the details below.
                  We may take steps to verify your identity and parental
                  authority.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  International Transfers
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  If information is transferred across borders, we use
                  safeguards required by applicable law (for example, standard
                  contractual clauses) to protect it.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Changes to This Policy
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  We may update this Privacy Policy from time to time. We will
                  post the updated version with a new “Last updated” date and
                  provide additional notice for material changes where required
                  by law.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Contact Us
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  For questions, requests, or privacy concerns, contact us at:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p className="text-gray-700">
                    <strong>Email:</strong> laila@amblyfy.com
                    <br />
                    <strong>Address:</strong> Lastādijas 12-k3, Rīga, Latvia
                    <br />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="fixed bottom-8 right-8 bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 focus:outline-none focus:ring-4 focus:ring-brand-cyan/50 transition-all duration-300 transform backdrop-blur-sm"
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 w-full">
        <div className="w-full px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-4">
            <p className="text-gray-600">
              &copy; 2025 Amblyfy. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/privacy-policy"
                className="text-brand-dark-blue font-medium"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/evidence-statement"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Evidence Statement
              </a>
              <a
                href="/activity-tracking-guide"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Activity Tracking Guide
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default PrivacyPolicy;
